import { ActionButton, ActionButtonType, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { GetSharedSpacesToProcessTemplateBySlugBffResponse, GitRef, ProcessTemplateResource, ProcessTemplateVersionResource, PublishProcessTemplateBffCommand, Repository, ShareTemplateToManySpacesBffCommand, } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useState } from "react";
import semver from "semver/preload";
import type { EditProcessTemplateLookupData } from "~/areas/blueprints/EditProcessTemplatePage";
import type { ProcessTemplateWizardPageProps } from "~/areas/blueprints/ProcessTemplateWizardDialogLayout";
import ProcessTemplateWizardDialogLayout from "~/areas/blueprints/ProcessTemplateWizardDialogLayout";
import { PublishTemplateDialog } from "~/areas/blueprints/PublishTemplateDialog";
import type { SharedSpaces } from "~/areas/blueprints/ShareTemplateDialog";
import { ShareTemplateDialog, ShareTemplateSpacesType } from "~/areas/blueprints/ShareTemplateDialog";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorActions } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { CommitSummaryAndDetails } from "~/areas/projects/components/VersionControl/CommitDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import CustomDialog from "~/components/Dialog/CustomDialog";
function getShareType(sharedSpaces: SharedSpaces): ShareTemplateSpacesType {
    if (sharedSpaces.shareToAll) {
        return ShareTemplateSpacesType.ShareAll;
    }
    if (sharedSpaces.individualSpacesSharedTo.length > 0) {
        return ShareTemplateSpacesType.ShareIndividual;
    }
    return ShareTemplateSpacesType.NotShared;
}
export enum ProcessTemplateWizardSteps {
    "Commit",
    "Publish",
    "Share"
}
interface ProcessTemplateWizardDialogProps {
    open: boolean;
    busy: boolean;
    errors: Error[];
    model: ProcessTemplateResource;
    gitRef: GitRef;
    lookupData: EditProcessTemplateLookupData;
    sharedSpaces: GetSharedSpacesToProcessTemplateBySlugBffResponse | null;
    reloadDataAfterSave: () => void;
    stepsToShow: ProcessTemplateWizardSteps[];
    publishedVersions: ProcessTemplateVersionResource[];
    getDefaultCommitMessage: () => string;
    commit: (isNavigationConfirmation: boolean, commitMessage?: CommitMessageWithDetails) => Promise<void>;
    setShowInitialSuccessCallout: (show: boolean) => void;
    closeDialog: () => void;
}
enum ActionCompleted {
    Committed = "committed",
    Published = "published",
    Shared = "shared"
}
export function ProcessTemplateWizardDialog({ busy, errors, gitRef, model, open, lookupData, sharedSpaces, reloadDataAfterSave, stepsToShow, publishedVersions, getDefaultCommitMessage, commit, setShowInitialSuccessCallout, closeDialog, }: ProcessTemplateWizardDialogProps) {
    const defaultedSharedSpaces = { shareToAll: sharedSpaces?.SharedToAllSpaces ?? false, individualSpacesSharedTo: sharedSpaces?.IndividuallySharedSpaceIds ?? [] };
    const emptyCommitMessage = { summary: "", details: "" };
    const defaultVersion = "1.0.0";
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const process = useProcessContext();
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>(defaultedSharedSpaces.individualSpacesSharedTo);
    const [shareType, setShareType] = useState<ShareTemplateSpacesType>(getShareType(defaultedSharedSpaces));
    const [canSaveShare, setCanSaveShare] = useState<boolean>(false);
    const latestNonPreReleaseVersion = publishedVersions.find((v) => !semver.prerelease(v.Version));
    const [versionToPublish, setVersionToPublish] = useState<string>(latestNonPreReleaseVersion ? semver.inc(latestNonPreReleaseVersion.Version, "major") ?? defaultVersion : defaultVersion);
    const [isPreRelease, setIsPreRelease] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>(emptyCommitMessage);
    const showCommit = stepsToShow.includes(ProcessTemplateWizardSteps.Commit);
    const showPublish = stepsToShow.includes(ProcessTemplateWizardSteps.Publish);
    const showShare = stepsToShow.includes(ProcessTemplateWizardSteps.Share);
    function createSnackbarMessage(actionsCompleted: ActionCompleted[]) {
        let snackbarMessage = "";
        if (actionsCompleted.length > 0) {
            snackbarMessage = `Successfully `;
            for (let i = 1; i <= actionsCompleted.length; i++) {
                snackbarMessage += `${actionsCompleted[i - 1]}`;
                if (actionsCompleted.length >= 2 && i < actionsCompleted.length) {
                    if (i === actionsCompleted.length - 1) {
                        snackbarMessage += " and ";
                    }
                    else {
                        snackbarMessage += ", ";
                    }
                }
            }
            snackbarMessage += " the process template.";
        }
        return snackbarMessage;
    }
    const { execute: handleSave } = useMutation({
        name: "Save process template",
        action: async (repository: Repository, isCommitOnly: boolean) => {
            const actionsCompleted: ActionCompleted[] = [];
            if (showCommit) {
                await commit(false, commitMessage);
                actionsCompleted.push(ActionCompleted.Committed);
            }
            if (isCommitOnly) {
                return { snackbarMessage: createSnackbarMessage(actionsCompleted), actionsCompleted };
            }
            if (showPublish) {
                await handlePublish(repository, versionToPublish, isPreRelease);
                actionsCompleted.push(ActionCompleted.Published);
            }
            if (showShare) {
                const shareToAll = shareType === ShareTemplateSpacesType.ShareAll;
                const notShared = shareType === ShareTemplateSpacesType.NotShared;
                await handleShare(repository, { shareToAll, individualSpacesSharedTo: shareToAll || notShared ? [] : selectedSpaces });
                actionsCompleted.push(ActionCompleted.Shared);
            }
            return { snackbarMessage: createSnackbarMessage(actionsCompleted), actionsCompleted };
        },
        afterComplete: async (repository, output) => {
            if (output.actionsCompleted.includes(ActionCompleted.Committed) && output.actionsCompleted.includes(ActionCompleted.Published) && output.actionsCompleted.includes(ActionCompleted.Shared)) {
                setShowInitialSuccessCallout(true);
            }
            showSnackbar(output.snackbarMessage);
            reloadDataAfterSave();
            resetState();
            errorActions.clearErrors();
            warningActions.clearWarnings();
        },
        onError: (error) => {
            errorActions.setErrors(getErrorsFromError(error), process.selectors);
            warningActions.clearWarnings();
        },
    });
    const handlePublish = async (repository: Repository, version: string, isPreRelease: boolean) => {
        const publishVersion: PublishProcessTemplateBffCommand = {
            ProcessTemplateSlug: model.Slug,
            GitRef: model.GitRef,
            Version: version,
            IsPreRelease: isPreRelease,
        };
        return await repository.ProcessTemplates.publishVersion(publishVersion);
    };
    const handleShare = async (repository: Repository, sharedSpaces: SharedSpaces) => {
        const shareCommand: ShareTemplateToManySpacesBffCommand = {
            ProcessTemplateSlug: model.Slug,
            GitRef: model.GitRef,
            ShareToAllSpaces: sharedSpaces.shareToAll,
            IndividuallySharedSpaceIds: sharedSpaces.individualSpacesSharedTo,
        };
        return await repository.ProcessTemplates.shareToSpace(shareCommand);
    };
    function resetState() {
        setVersionToPublish(latestNonPreReleaseVersion ? semver.inc(latestNonPreReleaseVersion.Version, "major") ?? defaultVersion : defaultVersion);
        setIsPreRelease(false);
        setSelectedSpaces(defaultedSharedSpaces.individualSpacesSharedTo);
        setShareType(getShareType(defaultedSharedSpaces));
        setCanSaveShare(false);
        setCommitMessage(emptyCommitMessage);
    }
    const pages: ProcessTemplateWizardPageProps[] = [];
    if (showCommit) {
        pages.push({
            title: "Commit",
            content: (<>
                    <CommitSummaryAndDetails onCommitMessageChanged={setCommitMessage} defaultSummary={getDefaultCommitMessage()} commitMessage={commitMessage} commitDetailsAccessibleName={`Commit details for saving the process template process`} commitMessageAccessibleName={`Commit message for saving the process template process`}/>

                    <p>
                        Octopus will commit the changes to the branch <code>{gitRef}</code>
                    </p>
                </>),
            actionAvailable: true,
            additionalActions: [
                <ActionButton key="Commit Only" label="Commit Only" disabled={busy} onClick={async () => {
                        await handleSave(true);
                        closeDialog();
                    }} type={ActionButtonType.Secondary}/>,
            ],
            statusBarLabel: "Commit",
            hideAdditionalActionsOnSinglePage: true,
            singlePageSaveLabel: "Commit",
            singlePageSavingLabel: "Committing...",
        });
    }
    if (showPublish) {
        pages.push({
            title: "Publish version",
            content: (<PublishTemplateDialog gitRef={gitRef} processTemplate={model} publishedVersions={publishedVersions} versionToPublish={versionToPublish} setVersionToPublish={setVersionToPublish} latestNonPreReleaseVersion={latestNonPreReleaseVersion} isPreRelease={isPreRelease} setIsPreRelease={setIsPreRelease}/>),
            actionAvailable: versionToPublish !== "",
            statusBarLabel: "Publish",
            singlePageSaveLabel: "Publish",
            singlePageSavingLabel: "Publishing...",
        });
    }
    if (showShare) {
        pages.push({
            title: "Share to...",
            content: <ShareTemplateDialog spaces={lookupData.spaces} shareType={shareType} selectedSpaces={selectedSpaces} setShareType={setShareType} setSelectedSpaces={setSelectedSpaces} setCanSave={setCanSaveShare}/>,
            actionAvailable: canSaveShare,
            statusBarLabel: "Share",
            singlePageSaveLabel: "Share",
            singlePageSavingLabel: "Sharing...",
        });
    }
    return (<CustomDialog open={open} close={noOp} render={(props) => (<ProcessTemplateWizardDialogLayout onSaveClick={async () => {
                await handleSave(false);
                return true;
            }} busy={busy} errors={errors} savePermission={{ permission: Permission.PlatformHubEdit }} resetState={resetState} pages={pages}/>)}/>);
}
